import {
  HoldAuditTrailDto,
  HoldReaction,
  HoldRestrictionType,
  HoldTypeRestrictionDto,
} from '@planning/app/api'
import { ContainerItemStore } from '@planning/rt-stores/container/ContainerItemStore'
import { holdService } from '@planning/services'
import _ from 'lodash'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { HoldItemStore } from '../../../rt-stores/hold/HoldItemStore'
import { mapToTableData } from '../models/table.model'
import { ContainerJourneyItemStore } from './../../Issues/Stores/ContainerJourneyItemStore'

interface OverrideContext {
  holdId: number
  holdTypeName: string
  containerNumber: string
  blockRestrictions: HoldTypeRestrictionDto[]
}

export class HoldsViewStore {
  isDialogOpen: boolean = false
  selectedHoldIds: number[] | null = null
  isOverrideDialogOpen: boolean = false
  selectedOverrideContext: OverrideContext | null = null
  auditTrailByHoldId = new Map<number, HoldAuditTrailDto | 'loading'>()

  constructor(
    private holdItemStore: HoldItemStore,
    private containerJourneyItemStore: ContainerJourneyItemStore,
    private containerItemStore: ContainerItemStore,
  ) {
    makeObservable(this, {
      isDialogOpen: observable,
      selectedHoldIds: observable,
      isOverrideDialogOpen: observable,
      selectedOverrideContext: observable,
      auditTrailByHoldId: observable,
      items: computed,
      uniqueHoldTypes: computed,
      openDialog: action,
      closeDialog: action,
      openOverrideDialog: action,
      closeOverrideDialog: action,
      setSelectedHolds: action,
      bulkResolve: action,
      fetch: action,
      fetchAuditTrail: action,
    })
  }

  get uniqueHoldTypes(): string[] {
    return _.uniq(
      _.values(this.holdItemStore.elements)
        .map(h => h.data.holdType?.name)
        .filter(Boolean) as string[],
    ).sort()
  }

  get items() {
    return _.values(this.holdItemStore.elements).map(mapToTableData)
  }

  openDialog = (ids: number[]) => {
    this.isDialogOpen = true
    this.selectedHoldIds = ids
  }

  closeDialog = () => {
    this.isDialogOpen = false
    this.selectedHoldIds = null
  }

  openOverrideDialog = (holdId: number) => {
    const hold = this.holdItemStore.elements[holdId]
    const restrictionsDict = hold?.data.holdType.restrictions ?? {}
    const blockRestrictions = Object.entries(restrictionsDict)
      .filter(([, reaction]) => reaction === HoldReaction.Block)
      .map(([restrictionType, reaction]) => ({
        restrictionType: restrictionType as HoldRestrictionType,
        reaction,
      }))

    this.selectedOverrideContext = {
      holdId,
      holdTypeName: hold?.data.holdType.name ?? '',
      containerNumber: hold?.container?.data?.number ?? '',
      blockRestrictions,
    }
    this.isOverrideDialogOpen = true
  }

  closeOverrideDialog = () => {
    this.isOverrideDialogOpen = false
    this.selectedOverrideContext = null
  }

  setSelectedHolds = (ids: number[] | null) => {
    this.selectedHoldIds = ids
  }

  fetch = async () => {
    await this.holdItemStore.fetch(true)
    const journeyIds = _.uniq(
      _.values(this.holdItemStore.elements).map(e => e.data.containerJourneyId),
    )
    if (journeyIds.length) {
      await this.containerJourneyItemStore.fetch([], true, journeyIds)
    }
    const containerIds = _.values(this.containerJourneyItemStore.elements).map(
      e => e.data.containerId,
    )
    if (containerIds.length) {
      await this.containerItemStore.fetchByIds(containerIds)
    }
  }

  fetchAuditTrail = async (holdId: number) => {
    if (this.auditTrailByHoldId.has(holdId)) return
    runInAction(() => this.auditTrailByHoldId.set(holdId, 'loading'))
    try {
      const data = await holdService.getAuditTrailByHoldId(holdId)
      runInAction(() => this.auditTrailByHoldId.set(holdId, data))
    } catch {
      runInAction(() => this.auditTrailByHoldId.delete(holdId))
    }
  }

  resolve = async (id: number, containerJourneyId: number, reason?: string) => {
    await holdService.approve({ ids: [id], containerJourneyId, reason })
  }

  bulkResolve = async (reason?: string) => {
    if (!this.selectedHoldIds) return
    await holdService.bulkApprove({ ids: this.selectedHoldIds, reason })
    this.setSelectedHolds(null)
    this.closeDialog()
  }

  override = async (operationTypes: HoldRestrictionType[], reason: string) => {
    if (!this.selectedOverrideContext) return
    await holdService.override({
      holdIds: [this.selectedOverrideContext.holdId],
      operationTypes,
      reason,
    })
    this.closeOverrideDialog()
  }
}
